import type { GradeBand } from './care';

/**
 * Short tasks for the time in a shelter (about ten minutes): a text to read with questions, or a
 * few puzzles. They are part of the app itself, so they open with no connection at all — a
 * shelter often has none. Self-check only, nothing is graded or sent anywhere. The texts are our
 * own; in production the school's methodologist adds tasks of their own.
 */
export interface ShelterQuestion {
  text: string;
  options: string[];
  correct: number;
}

export interface ShelterTask {
  id: string;
  band: GradeBand;
  kind: 'reading' | 'puzzle';
  title: string;
  minutes: number;
  /** paragraphs; puzzles have a line of introduction */
  text: string[];
  questions: ShelterQuestion[];
}

export const SHELTER_TASKS: readonly ShelterTask[] = [
  {
    id: 'hedgehog',
    band: '1-4',
    kind: 'reading',
    title: 'Їжачок готується до зими',
    minutes: 5,
    text: [
      'Восени їжачок Пиріжок почав готуватися до зими. Він збирав сухе листя й носив його під старий пеньок. Там було тихо й затишно.',
      'Їжачок їв багато жуків і равликів, щоб стати пухкеньким. Узимку він спатиме й не зможе шукати їжу.',
      'Коли випав перший сніг, Пиріжок згорнувся клубочком у своїй мʼякій хатинці. «До весни!» — позіхнув він і заплющив очі.',
    ],
    questions: [
      {
        text: 'Де їжачок зробив собі хатинку?',
        options: ['На дереві', 'Під старим пеньком', 'У норі лисиці'],
        correct: 1,
      },
      {
        text: 'Навіщо їжачок багато їв восени?',
        options: ['Щоб швидше бігати', 'Щоб пригостити друзів', 'Бо взимку він спатиме'],
        correct: 2,
      },
      {
        text: 'Що сказав Пиріжок, коли випав сніг?',
        options: ['«Добраніч!»', '«До весни!»', '«Привіт, зимо!»'],
        correct: 1,
      },
    ],
  },
  {
    id: 'riddles-small',
    band: '1-4',
    kind: 'puzzle',
    title: 'Порахуй і подумай',
    minutes: 5,
    text: ['Розвʼяжи задачки. Не поспішай — часу досить.'],
    questions: [
      {
        text: 'У кошику 3 яблука і 2 груші. Скільки фруктів у кошику?',
        options: ['4', '5', '6'],
        correct: 1,
      },
      {
        text: 'Що важче: кілограм пірʼя чи кілограм цвяхів?',
        options: ['Цвяхи', 'Пірʼя', 'Однаково'],
        correct: 2,
      },
      {
        text: 'Яке число пропущено: 2, 4, 6, …, 10?',
        options: ['7', '8', '9'],
        correct: 1,
      },
    ],
  },
  {
    id: 'fireflies',
    band: '5-9',
    kind: 'reading',
    title: 'Чому світлячки світяться',
    minutes: 7,
    text: [
      'Теплої літньої ночі в траві можна побачити маленькі вогники — це світлячки. Вони не мають ні лампочок, ні батарейок, але все одно світяться.',
      'У черевці світлячка відбувається хімічна реакція: речовина люциферин зʼєднується з киснем, а допомагає їй особливий білок — люцифераза. Енергія реакції виділяється у вигляді світла.',
      'Таке світло називають «холодним»: майже вся енергія стає світлом, а не теплом. Звичайна лампа розжарювання, навпаки, більшу частину енергії перетворює на тепло.',
      'Світлячки світяться, щоб знайти одне одного: кожен вид має свій «код» спалахів — майже як азбука Морзе.',
    ],
    questions: [
      {
        text: 'Що зʼєднується з киснем у черевці світлячка?',
        options: ['Люциферин', 'Хлорофіл', 'Вода'],
        correct: 0,
      },
      {
        text: 'Чому світло світлячка називають «холодним»?',
        options: [
          'Світлячки живуть лише взимку',
          'Майже вся енергія йде на світло, а не на тепло',
          'Воно синього кольору',
        ],
        correct: 1,
      },
      {
        text: 'Навіщо світлячкам спалахи?',
        options: ['Щоб гріти траву', 'Щоб лякати птахів', 'Щоб знаходити одне одного'],
        correct: 2,
      },
    ],
  },
  {
    id: 'logic-middle',
    band: '5-9',
    kind: 'puzzle',
    title: 'Логіка без інтернету',
    minutes: 7,
    text: ['Три задачі на кмітливість. Можна рахувати на пальцях — це не соромно.'],
    questions: [
      {
        text: 'Тато старший за сина на 28 років. Якою буде різниця у віці через 10 років?',
        options: ['38 років', '28 років', '18 років'],
        correct: 1,
      },
      {
        text: 'Продовж ряд: 1, 1, 2, 3, 5, 8, …',
        options: ['11', '12', '13'],
        correct: 2,
      },
      {
        text: 'У кімнаті 4 кути. У кожному куті сидить кіт. Навпроти кожного кота — 3 коти. Скільки котів у кімнаті?',
        options: ['4', '12', '16'],
        correct: 0,
      },
    ],
  },
  {
    id: 'stress-brain',
    band: '10-11',
    kind: 'reading',
    title: 'Як мозок справляється зі стресом',
    minutes: 8,
    text: [
      'Коли ми хвилюємося, мозок вмикає «режим тривоги»: серце бʼється швидше, дихання частішає, мʼязи напружуються. Це давній механізм, який колись допомагав людям тікати від небезпеки.',
      'Реакцію запускає мигдалеподібне тіло — невелика ділянка мозку. А префронтальна кора, яка відповідає за планування й рішення, у стресі працює гірше. Тому під час хвилювання буває важко зосередитися.',
      'Повільне дихання з довгим видихом подає тілу сигнал «небезпеки немає»: вмикається парасимпатична нервова система, і серцебиття сповільнюється. Тож дихальні вправи справді заспокоюють, а не лише відволікають.',
      'Сон, рух і розмова з людьми, яким довіряєш, допомагають мозку швидше повертатися до спокою.',
    ],
    questions: [
      {
        text: 'Яка ділянка мозку запускає реакцію тривоги?',
        options: ['Мозочок', 'Мигдалеподібне тіло', 'Префронтальна кора'],
        correct: 1,
      },
      {
        text: 'Чому в стресі важко зосередитися?',
        options: ['Префронтальна кора працює гірше', 'Мозку бракує світла', 'Через голод'],
        correct: 0,
      },
      {
        text: 'Що робить повільний довгий видих?',
        options: [
          'Пришвидшує серце',
          'Нічого, це лише відволікання',
          'Вмикає парасимпатичну систему й сповільнює серцебиття',
        ],
        correct: 2,
      },
    ],
  },
  {
    id: 'logic-senior',
    band: '10-11',
    kind: 'puzzle',
    title: 'Задачі на кмітливість',
    minutes: 8,
    text: ['Без калькулятора. Округлювати не потрібно.'],
    questions: [
      {
        text: 'Скільки буде 15% від 80?',
        options: ['10', '12', '15'],
        correct: 1,
      },
      {
        text: 'Поїзд проїжджає 60 км за 45 хвилин. Яка його швидкість?',
        options: ['75 км/год', '80 км/год', '90 км/год'],
        correct: 1,
      },
      {
        text: 'Усі ромби — паралелограми. Ця фігура — не паралелограм. Отже, вона…',
        options: ['не ромб', 'ромб', 'квадрат'],
        correct: 0,
      },
    ],
  },
];

/** The tasks of a class's age group. */
export function shelterTasksFor(grade: number | undefined): ShelterTask[] {
  const band: GradeBand = (grade ?? 5) <= 4 ? '1-4' : (grade ?? 5) <= 9 ? '5-9' : '10-11';
  return SHELTER_TASKS.filter((task) => task.band === band);
}
